import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'

import { AREA_CODE_OPTIONS } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { startOfTodayJst } from '~/models/dateTime'
import { defineFilterCodec, isoDate, oneOf } from '~/models/filterCodec'
import {
  DEFAULT_DISPLAY_UNIT,
  type DisplayUnit,
} from '~/pages/Supply48Data/models/displayUnit'
import { CATEGORY_OPTIONS } from '~/pages/Supply48Data/models/filterOptions'

/**
 * 48点データのフィルタ state (in-memory 形状)。画面遷移・再訪をまたいで保持する
 * 対象 (AC-SC-031 [P0])。
 *
 * この画面はサイドバーの「元データを開く」で取引契約管理・スポット価格へ抜け、
 * 逆に相対取引一覧からは deep link で戻ってくる。保持しないと往復のたびに
 * 売買区分・区分・エリア・対象日がすべて初期値へ戻る。
 *
 * ## 表示単位を含める理由
 *
 * `displayUnit` (kWh/30min ↔ kW) は絞り込みではないが、design
 * `supply/48data.html` でも `.filter-group` + `.filter-label` として同じ
 * サイドバーに置かれ、**LS `ebalance-s48-unit` に永続化**されている。
 * 2 値の単一選択なので AC-SC-031 の「選択状態」(同 §6 機能詳細 2) に当たる。
 * 保存先は他画面と揃えて本 module の 1 blob にまとめる (design がキーを分けて
 * いるのは素の JS 実装の都合で、キー名は利用者に見えない)。
 *
 * ## 保持しないもの
 *
 * - **グラフ表示トグル** (`showChart`) — design 側も LS キーを持たない意図的な非永続
 * - **グラフ対象 leg** (`chartTargetLegId`) — データ到着後に先頭を自動選択する派生値
 * - **グラフ幅** — `useResizableChartWidth` が既に別キーで永続化済み。二重に持たない
 * - **編集セッション** (編集値 / 履歴 / 選択範囲 / 各ダイアログ) — フィルタではない
 */
export const SUPPLY_48_DATA_FILTERS_STORAGE_KEY =
  'ebalance-supply-48-data-filters'

export type Supply48DataFilters = {
  buySell: Side
  category: SupplyCategory
  selectedArea: AreaCode
  /** 対象日 (単日固定、AC-S4D-002)。 */
  targetDate: Date
  displayUnit: DisplayUnit
}

/**
 * フィルタ既定値。
 *
 * エリアの既定が `UNSPECIFIED` なのは「業務エリア設定の取得完了まで一覧を引かない」
 * ための番人 (§8 で area fallback を廃止済み)。取得後に呼び出し側の effect が
 * 先頭エリアへ寄せる。
 */
export function defaultSupply48DataFilters(
  now: Date = serverNow(),
): Supply48DataFilters {
  return {
    buySell: Side.SELL,
    category: SupplyCategory.BILATERAL,
    selectedArea: AreaCode.UNSPECIFIED,
    targetDate: startOfTodayJst(now),
    displayUnit: DEFAULT_DISPLAY_UNIT,
  }
}

/**
 * 保存形式は `{ buySell, category, selectedArea, targetDate: 'YYYY-MM-DD',
 * displayUnit }`。壊れた値・未知 enum は項目単位で既定へ落ちる。
 *
 * **エリアは固定 enum で検証するだけで、業務エリア設定とは突き合わせない。**
 * 業務エリアはサーバー由来で純関数からは見えないため、候補外エリアを先頭へ寄せる
 * のは呼び出し側の effect の責務 (`useSupply48Filters`)。
 */
export const supply48DataFiltersCodec = defineFilterCodec<Supply48DataFilters>({
  defaults: defaultSupply48DataFilters,
  fields: {
    buySell: oneOf<Side>([Side.SELL, Side.BUY]),
    category: oneOf<SupplyCategory>(
      CATEGORY_OPTIONS.map((option) => option.value),
    ),
    selectedArea: oneOf<AreaCode>(AREA_CODE_OPTIONS),
    targetDate: isoDate(),
    displayUnit: oneOf<DisplayUnit>(['kWh30min', 'kW']),
  },
})
